import { useCallback, useEffect, useMemo, useState } from "react";
import type { DocFragment, DocTypes, DocWithId, IndexKeyType, FPIndexRow, MapFn, Database } from "@fireproof/core-types-base";
import type { LiveQueryResult } from "./types.js";

/**
 * Implementation of the useLiveQuery hook
 */
export function createUseLiveQuery(database: Database) {
  return function useLiveQuery<T extends DocTypes, K extends IndexKeyType = string, R extends DocFragment = T>(
    mapFn: MapFn<T> | string,
    query = {},
    initialRows: FPIndexRow<K, T, R>[] = [],
  ): LiveQueryResult<T, K, R> {
    const [hydrated, setHydrated] = useState(false);

    const [result, setResult] = useState<Omit<LiveQueryResult<T, K, R>, "hydrated">>(() => ({
      docs: initialRows.map((r) => r.doc).filter((r): r is DocWithId<T> => !!r),
      rows: initialRows,
    }));

    const queryString = useMemo(() => JSON.stringify(query), [query]);
    const mapFnString = useMemo(() => mapFn.toString(), [mapFn]);

    useEffect(() => {
      setHydrated(false);
    }, [mapFnString, queryString]);

    const refreshRows = useCallback(async () => {
      const res = await database.query<T, K, R>(mapFn, { ...query, includeDocs: true });
      database.logger.Debug().Str("dbname", database.name).Uint64("rows", res.rows.length).Msg("Refreshing live query");
      setResult(res);
      setHydrated(true);
    }, [database, mapFnString, queryString]);

    useEffect(() => {
      database.logger.Debug().Str("dbname", database.name).Msg("Initializing live query subscription");
      const unsubscribe = database.subscribe(refreshRows);
      return () => {
        unsubscribe();
      };
    }, [database, refreshRows]);

    useEffect(() => {
      refreshRows();
    }, [database, queryString, mapFnString]);

    return { ...result, hydrated };
  };
}
